import type { StoryCtx } from './ctx.js';
import { mentionsIn, refIndex } from './refs.js';
import { stripSpans } from './spans.js';
import type { Entity, FieldValue, IgnoredTerm, TextDocument } from './types.js';

// ---------------------------------------------------------------------------
// Vorkommen: woran ein Element im Text zu erkennen ist (sein Name und gesammelte Wörter). Stellen, an denen ein Vorkommen steht, aber
// nicht verknüpft ist, werden als mögliche Verweise angeboten; bewusst nicht verknüpfte merkt sich das Dokument samt Nachbarwörtern.
// ---------------------------------------------------------------------------

/** Höchstlänge eines Vorkommens (und eines gemerkten Nachbarworts) */
export const TERM_MAX = 60;
/** Höchstzahl gesammelter Vorkommen je Element */
export const TERMS_PER_ENTITY = 50;
/** Höchstzahl gemerkter, bewusst nicht verknüpfter Stellen je Dokument */
export const IGNORED_PER_DOCUMENT = 500;
/** Längere verknüpfte Passagen sind keine Bezeichnung, sondern Handlung („Lisa steigt auf den Turm“) und werden nicht gesammelt */
const MAX_TERM_WORDS = 4;

const WORD_CHAR = /[\p{L}\p{N}_-]/u;
const WORDS = /[\p{L}\p{N}_-]+/gu;
const KEY = '[A-Za-z][A-Za-z0-9_]{0,29}';

/** Leerraum zusammenfassen, Rand weg */
export const normalizeTerm = (s: string): string => s.replace(/\s+/g, ' ').trim();

/** Artikel: allein nie ein Vorkommen („der“ aus „der Magier“), nur zusammen mit dem Wort danach */
const ARTICLES = new Set(['der', 'die', 'das', 'den', 'dem', 'des', 'ein', 'eine', 'einen', 'einem', 'einer', 'eines', 'the', 'a', 'an']);

/** Prüft ein Vorkommen; liefert eine Meldung oder null. */
export function checkTerm(raw: string): string | null {
  const t = normalizeTerm(raw);
  if (t.length < 2) return 'Ein Vorkommen braucht mindestens zwei Zeichen.';
  if (t.length > TERM_MAX) return `Ein Vorkommen darf höchstens ${TERM_MAX} Zeichen haben.`;
  if (!/\p{L}/u.test(t)) return 'Ein Vorkommen braucht mindestens einen Buchstaben.';
  if (/[[\]@]/.test(t)) return 'Eckige Klammern und „@“ gehören nicht in ein Vorkommen.';
  if (!WORD_CHAR.test(t[0]!) || !WORD_CHAR.test(t[t.length - 1]!)) return 'Ein Vorkommen beginnt und endet mit einem Buchstaben oder einer Ziffer.';
  if (ARTICLES.has(t.toLowerCase())) return 'Ein Artikel allein ist kein Vorkommen – nur zusammen mit dem Wort danach („der Magier“).';
  return null;
}

/** „der Magier“ steht am Satzanfang als „Der Magier“: die Form mit großem Anfangsbuchstaben (sonst null) */
const capitalized = (t: string): string | null => {
  const up = t[0]!.toUpperCase() + t.slice(1);
  return up !== t ? up : null;
};

/** Alle Schreibweisen, unter denen ein Vorkommen im Text steht (klein beginnende auch am Satzanfang) */
const spellings = (t: string): string[] => {
  const up = capitalized(t);
  return up ? [t, up] : [t];
};

/** Name eines Elements (Titelfeld), sonst leer */
const nameOf = (ctx: StoryCtx, e: Entity): string => normalizeTerm(String(e.fields[ctx.types.get(e.typeId)?.titleKey ?? ''] ?? ''));

/** Foundationals (Prämisse, Welt) hängen an allem; ihr Name („Welt“) ist kein Hinweis auf einen fehlenden Verweis. */
const searchable = (ctx: StoryCtx, e: Entity): boolean => {
  const category = ctx.types.get(e.typeId)?.category;
  return category !== undefined && category !== 'foundational';
};

/** Woran ein Element im Text zu erkennen ist: sein Name und seine Vorkommen (gültige, ohne Doppelte). Foundationals: keine. */
export function termsOf(ctx: StoryCtx, e: Entity): string[] {
  if (!searchable(ctx, e)) return [];
  return [...new Set([nameOf(ctx, e), ...(e.terms ?? []).map(normalizeTerm)])].filter((t) => checkTerm(t) === null);
}

export interface TermIndex {
  /** Vorkommen nach ihrem ersten Wort, je Wort die längsten zuerst (ein Vorkommen kann mehreren Elementen gehören) */
  byFirst: Map<string, { term: string; entityIds: string[] }[]>;
}

/** Verzeichnis aus Vorkommen → Elemente; `pairs` liefert je Element seine Vorkommen. */
function indexOf(pairs: Iterable<[string, string[]]>): TermIndex {
  const owners = new Map<string, string[]>();
  for (const [id, terms] of pairs) for (const t of terms.flatMap(spellings)) if (!owners.get(t)?.includes(id)) owners.set(t, [...(owners.get(t) ?? []), id]);
  const byFirst = new Map<string, { term: string; entityIds: string[] }[]>();
  for (const [term, entityIds] of owners) {
    const first = term.match(WORDS)?.[0];
    if (!first) continue;
    byFirst.set(first, [...(byFirst.get(first) ?? []), { term, entityIds }]);
  }
  for (const list of byFirst.values()) list.sort((a, b) => b.term.length - a.term.length);
  return { byFirst };
}

/** Alle Vorkommen der Geschichte (Namen und gesammelte Wörter der Elemente). */
export const termIndex = (ctx: StoryCtx): TermIndex => indexOf(ctx.story.entities.map((e) => [e.id, termsOf(ctx, e)]));

export interface VisibleText {
  /** Text, wie er im Editor zu lesen ist (Zeilen bleiben Zeilen) */
  text: string;
  /** Bereiche, die schon verknüpft sind oder ein Verweis sind (`[@schlüssel]`), als [von, bis) */
  linked: [number, number][];
  /** Schlüssel je verknüpftem Bereich (gleiche Reihenfolge wie `linked`) */
  keys: string[];
}

/** Zeilenanfang einer Überschrift, eines Zitats, einer Liste bzw. Aufgabe */
const BLOCK_PREFIX = /^(?:\s*(?:#{1,6}\s+|>\s?|[-*+]\s+(?:\[[ xX]\]\s+)?|\d+[.)]\s+))*/;
/** Verknüpfte Passage, verknüpftes Wort, allein stehender Verweis, (ältere Texte) bloßes „@schlüssel“ */
const REFS = new RegExp(`\\[([^\\]\\n]+)\\]\\(@(${KEY})\\)|([\\p{L}\\p{N}_-]+)\\[@(${KEY})\\]|\\[@(${KEY})\\]|(?<![\\w@])@(${KEY})`, 'gu');
/** Formatierung, die im Editor nicht zu sehen ist: Hervorhebung, Code, Maskierung; Links zeigen ihren Text */
const inline = (s: string): string =>
  s
    .replace(/\[([^\]\n]*)\]\([^)\n]*\)/g, '$1')
    .replace(/\\([^\w\s])/g, '$1')
    .replace(/\*\*|~~|[*`]/g, '');

/** Markdown eines Dokuments als sichtbarer Text samt der schon verknüpften Bereiche (wie im Editor). */
export function visibleText(md: string): VisibleText {
  let text = '';
  const linked: [number, number][] = [];
  const keys: string[] = [];
  // Bezüge sind keine Verknüpfung des Wortes: ihr Text zählt wie normaler Text (Namen darin werden angeboten)
  stripSpans(md).split('\n').forEach((raw, i) => {
    if (i > 0) text += '\n';
    const line = raw.replace(BLOCK_PREFIX, '');
    let last = 0;
    for (const m of line.matchAll(REFS)) {
      text += inline(line.slice(last, m.index));
      const shown = m[1] !== undefined ? inline(m[1]) : m[3] !== undefined ? m[3] : m[0];
      linked.push([text.length, text.length + shown.length]);
      keys.push(m[2] ?? m[4] ?? m[5] ?? m[6] ?? '');
      text += shown;
      last = m.index + m[0].length;
    }
    text += inline(line.slice(last));
  });
  return { text, linked, keys };
}

export interface TermHit {
  from: number;
  to: number;
  term: string;
  /** Elemente, zu denen das Vorkommen gehört (meist eins) */
  entityIds: string[];
  /** Wort davor bzw. danach in derselben Zeile (leer: keins) */
  before: string;
  after: string;
}

/**
 * Nicht verknüpfte Vorkommen in einem Text: ganze Wörter (nicht „Clara“ in „Clara-Marie“), Groß- und Kleinschreibung wie gesammelt,
 * an jeder Stelle das längste passende Vorkommen. `linked` sind Bereiche, die schon verknüpft sind; `\n` trennt Zeilen bzw. Absätze.
 */
export function findTerms(text: string, linked: [number, number][], index: TermIndex): TermHit[] {
  if (index.byFirst.size === 0) return [];
  const words = [...text.matchAll(WORDS)];
  const isLinked = (from: number, to: number): boolean => linked.some(([a, b]) => from < b && to > a);
  const sameLine = (a: number, b: number): boolean => !text.slice(a, b).includes('\n');
  const out: TermHit[] = [];
  for (let i = 0; i < words.length; i++) {
    const w = words[i]!;
    const candidates = index.byFirst.get(w[0]);
    if (!candidates) continue;
    const from = w.index;
    const hit = candidates.find((c) => text.startsWith(c.term, from) && !WORD_CHAR.test(text[from + c.term.length] ?? ''));
    if (!hit) continue;
    const to = from + hit.term.length;
    let j = i;
    while (j + 1 < words.length && words[j + 1]!.index < to) j++;
    if (isLinked(from, to)) {
      i = j;
      continue;
    }
    const prev = words[i - 1];
    const next = words[j + 1];
    out.push({
      from,
      to,
      term: hit.term,
      entityIds: hit.entityIds,
      before: prev && sameLine(prev.index + prev[0].length, from) ? prev[0].slice(0, TERM_MAX) : '',
      after: next && sameLine(to, next.index) ? next[0].slice(0, TERM_MAX) : '',
    });
    i = j;
  }
  return out;
}

/** Ist diese Stelle im Dokument als „nicht verknüpfen“ gemerkt? */
export const isIgnored = (hit: Pick<TermHit, 'term' | 'before' | 'after'>, ignored: readonly IgnoredTerm[] | undefined): boolean =>
  !!ignored?.some((x) => x.term === hit.term && x.before === hit.before && x.after === hit.after);

/** Nicht verknüpfte, nicht ignorierte Vorkommen eines Dokuments. `visible` darf vorberechnet sein (siehe `visibleText`). */
export function unlinkedTerms(doc: Pick<TextDocument, 'text' | 'ignored'>, index: TermIndex, visible: VisibleText = visibleText(doc.text)): TermHit[] {
  return findTerms(visible.text, visible.linked, index).filter((h) => !isIgnored(h, doc.ignored));
}

/** Gemerkte Ausnahmen, die im (geänderten) Text noch vorkommen; die übrigen fallen weg. */
export function pruneIgnored(text: string, ignored: readonly IgnoredTerm[] | undefined): IgnoredTerm[] {
  if (!ignored || ignored.length === 0) return [];
  const v = visibleText(text);
  const hits = findTerms(v.text, v.linked, indexOf([['', [...new Set(ignored.filter((x) => !x.term.startsWith('@') && !x.term.startsWith('~')).map((x) => x.term))]]]));
  // „kein Ortswechsel“ (`{term: '@nach', before: '@von'}`, scenePlace.ts) gilt, solange der Text beide Orte nennt; „keine Änderung“
  // (`{term: '@wer', before: '!art'}`, states.ts), solange er das Element nennt; eine Zeitmarke „ist Absicht“ (`{term: '~zeit'}` bzw.
  // `{term: '~Wortlaut'}`, timeConflicts.ts), solange der Text Zeitmarken bzw. diesen Wortlaut hat; Text gegen Angabe (`~schauplatz`,
  // `~perspektive`, settingConflicts.ts), solange der Text den Ort nennt bzw. Gedanken hat; Head-Hopping (`~kopfwechsel`) und „ohne
  // Perspektive“ (`~ohne-perspektive`, perspective.ts), solange er Gedanken hat; „Perspektivfigur fehlt“ (`~abwesend`) bleibt
  const keys = new Set(mentionsIn(text).map((m) => m.key));
  const seen = new Set<string>();
  const kept = (x: IgnoredTerm): boolean => {
    if (x.term === '~zeit') return text.includes(']{zeit');
    // Text gegen Angabe „ist Absicht“ (settingConflicts.ts): solange der Text den Ort nennt bzw. Gedanken wiedergibt
    if (x.term === '~schauplatz') return keys.has(x.after.slice(1));
    if (x.term === '~perspektive' || x.term === '~kopfwechsel' || x.term === '~ohne-perspektive') return text.includes(' gedanke}');
    // Perspektivfigur fehlt, Figur lebt nicht, Besitz (stateConflicts.ts): hängen an Angaben der Szene, nicht am Text
    if (x.term === '~abwesend' || x.term === '~lebt' || x.term === '~besitz' || x.term === '~zugleich' || x.term === '~wissen') return true;
    if (x.term.startsWith('~')) return v.text.includes(x.term.slice(1));
    if (x.term.startsWith('@')) return keys.has(x.term.slice(1)) && (x.before.startsWith('!') || keys.has(x.before.slice(1)));
    return hits.some((h) => isIgnored(h, [x]));
  };
  return ignored.filter((x) => {
    const k = `${x.term}\n${x.before}\n${x.after}`;
    if (seen.has(k) || !kept(x)) return false;
    seen.add(k);
    return true;
  });
}

// ---------------------------------------------------------------------------
// Vorkommen sammeln: aus verknüpften Wörtern und kurzen Passagen
// ---------------------------------------------------------------------------

const LINKS = new RegExp(`\\[([^\\]\\n]+)\\]\\(@(${KEY})\\)|([\\p{L}\\p{N}_-]+)\\[@(${KEY})\\]`, 'gu');

interface LinkRun {
  key: string;
  /** die verknüpften Wörter bzw. die Passage */
  parts: string[];
  /** aus einzeln verknüpften Wörtern (Textfelder kennen nur verknüpfte Wörter) */
  words: boolean;
  end: number;
}

/**
 * Verknüpfungen eines Textes am Stück: eine Passage, ein Wort oder mehrere nebeneinander stehende, einzeln verknüpfte Wörter mit
 * demselben Schlüssel („Staubbeutel[@x] der[@x] Offenbarung[@x]“ – so stehen Wendungen in Textfeldern) als eine Wendung.
 */
function linkRuns(md: string): LinkRun[] {
  const runs: LinkRun[] = [];
  // Bezüge lernen nichts; die Nennungen in ihnen schon
  const text = stripSpans(md);
  for (const m of text.matchAll(LINKS)) {
    const key = (m[2] ?? m[4]!).toLowerCase();
    const prev = runs[runs.length - 1];
    if (m[3] !== undefined && prev?.words && prev.key === key && /^[ \t]+$/.test(text.slice(prev.end, m.index))) {
      prev.parts.push(m[3]);
      prev.end = m.index + m[0].length;
      continue;
    }
    runs.push({ key, parts: [m[1] !== undefined ? inline(m[1]) : m[3]!], words: m[3] !== undefined, end: m.index + m[0].length });
  }
  return runs;
}

/** Verknüpfte Wörter und kurze Wendungen eines Textes mit ihrem Schlüssel („Clara[@clara]“ → clara: „Clara“). */
export function linkedTerms(text: string): { key: string; term: string }[] {
  const out: { key: string; term: string }[] = [];
  for (const r of linkRuns(text)) {
    const term = normalizeTerm(r.parts.join(' '));
    if (checkTerm(term) !== null || (term.match(WORDS)?.length ?? 0) > MAX_TERM_WORDS) continue;
    out.push({ key: r.key, term });
  }
  return out;
}

/**
 * Bekannt, auch in der Schreibweise am Satzanfang: „der Magier“ deckt „Der Magier“ ab. Umgekehrt nicht – ein nur großgeschrieben
 * bekanntes „Der Elf“ findet „der Elf“ mitten im Satz nicht, die kleine Form wird also noch gelernt.
 */
const knows = (known: Set<string>, term: string): boolean => known.has(term) || [...known].some((k) => capitalized(k) === term);

/**
 * Wie ein verknüpftes Wort gelernt wird: Eine Wendung mit großgeschriebenem Artikel vorn („Der Elf“) stand am Satzanfang – gelernt wird
 * die kleine Form („der Elf“), die auch den Satzanfang trifft. Namen („Clara“, „Bran der Alte“) bleiben, wie sie sind.
 */
export function learnable(term: string): string {
  const [first, ...rest] = term.split(' ');
  return rest.length > 0 && first && ARTICLES.has(first.toLowerCase()) && first !== first.toLowerCase() ? first.toLowerCase() + term.slice(first.length) : term;
}

/** Vorkommen ohne Doppelte in der Schreibweise am Satzanfang: neben „der Elf“ fällt „Der Elf“ weg (die kleine Form trifft beide). */
export const foldSpellings = (terms: string[]): string[] => {
  const all = new Set(terms);
  return terms.filter((t) => ![...all].some((k) => k !== t && capitalized(k) === t));
};

/**
 * Vorkommen, die ein Element durch eine Änderung dazulernt: Wörter, die in `after` öfter mit ihm verknüpft sind als in `before` und
 * die es noch nicht kennt (Name und Vorkommen). Ergebnis: Element-ID → neue Vorkommen (in der Reihenfolge im Text).
 */
export function learnedTerms(ctx: StoryCtx, before: string[], after: string[]): Map<string, string[]> {
  const count = (texts: string[]): Map<string, number> => {
    const m = new Map<string, number>();
    for (const t of texts) for (const l of linkedTerms(t)) m.set(`${l.key}\n${l.term}`, (m.get(`${l.key}\n${l.term}`) ?? 0) + 1);
    return m;
  };
  const was = count(before);
  const refs = refIndex(ctx);
  const out = new Map<string, string[]>();
  for (const [k, n] of count(after)) {
    if (n <= (was.get(k) ?? 0)) continue;
    const [key, linked] = k.split('\n') as [string, string];
    const term = learnable(linked);
    const entity = refs.get(key);
    if (!entity || !searchable(ctx, entity)) continue;
    const known = new Set([...termsOf(ctx, entity), ...(out.get(entity.id) ?? [])]);
    if (knows(known, term) || (entity.terms?.length ?? 0) + (out.get(entity.id)?.length ?? 0) >= TERMS_PER_ENTITY) continue;
    out.set(entity.id, [...(out.get(entity.id) ?? []), term]);
  }
  return out;
}

/**
 * Vorkommen, die ein Element durch eine Änderung wieder verlernt (Gegenstück zu `learnedTerms`): von selbst gelernte (`learned`), deren
 * Verknüpfung mit ihm in `after` seltener vorkommt als in `before` und danach nirgends mehr in der Geschichte – auch nicht in der anderen
 * Schreibweise am Satzanfang. `all` = alle Texte der Geschichte vor der Änderung. Ergebnis: Element-ID → zu verlernende Vorkommen.
 */
export function unlearnedTerms(ctx: StoryCtx, before: string[], after: string[], all: string[] = allTexts(ctx)): Map<string, string[]> {
  const count = (texts: string[]): Map<string, number> => {
    const m = new Map<string, number>();
    for (const t of texts) for (const l of linkedTerms(t)) m.set(`${l.key}\n${l.term}`, (m.get(`${l.key}\n${l.term}`) ?? 0) + 1);
    return m;
  };
  const was = count(before);
  const now = count(after);
  const total = count(all);
  const refs = refIndex(ctx);
  const same = (a: string, b: string): boolean => a === b || capitalized(a) === b || capitalized(b) === a;
  /** Verknüpfungen mit `key` und diesem Vorkommen, die nach der Änderung in der ganzen Geschichte bleiben */
  const remaining = (key: string, term: string): number => {
    let n = 0;
    for (const k of new Set([...total.keys(), ...now.keys()])) {
      const [kk, tt] = k.split('\n') as [string, string];
      if (kk === key && same(tt, term)) n += (total.get(k) ?? 0) - (was.get(k) ?? 0) + (now.get(k) ?? 0);
    }
    return n;
  };
  const out = new Map<string, string[]>();
  for (const [k, n] of was) {
    if ((now.get(k) ?? 0) >= n) continue;
    const [key, term] = k.split('\n') as [string, string];
    const entity = refs.get(key);
    const learned = entity?.learned?.find((l) => same(normalizeTerm(l), term));
    if (!entity || !learned || remaining(key, term) > 0 || out.get(entity.id)?.includes(learned)) continue;
    out.set(entity.id, [...(out.get(entity.id) ?? []), learned]);
  }
  return out;
}

/** Alle Texte der Geschichte, in denen verknüpft werden kann: Dokumente und Felder der Nodes, Felder der Elemente. */
export function allTexts(ctx: StoryCtx): string[] {
  const str = (v: FieldValue | undefined): v is string => typeof v === 'string';
  return [
    ...ctx.story.nodes.flatMap((n) => [...Object.values(n.fields).filter(str), ...n.dynamicFields.map((f) => f.value).filter(str), ...n.documents.map((d) => d.text)]),
    ...ctx.story.entities.flatMap((e) => Object.values(e.fields).filter(str)),
  ];
}

/** Einmal für ältere Geschichten: Vorkommen aus allen bisherigen Verknüpfungen sammeln (Element-ID → neue Vorkommen). */
export const collectTerms = (ctx: StoryCtx): Map<string, string[]> => learnedTerms(ctx, [], allTexts(ctx));

/**
 * Einmal aufräumen: Bis die Wörter einer Wendung zusammen zählten, wurden sie einzeln gelernt („der“, „Offenbarung“ aus
 * „Staubbeutel der Offenbarung“). Weg fallen ungültige Vorkommen (ein Artikel allein) und Einzelwörter, die nur innerhalb einer
 * verknüpften Wendung stehen, nie für sich. Ergebnis: Element-ID → die bereinigten Vorkommen (nur Elemente, an denen sich etwas ändert).
 */
export function repairedTerms(ctx: StoryCtx): Map<string, string[]> {
  const inRun = new Map<string, Set<string>>();
  const alone = new Map<string, Set<string>>();
  const add = (m: Map<string, Set<string>>, key: string, w: string): void => void m.set(key, (m.get(key) ?? new Set()).add(w));
  for (const text of allTexts(ctx)) {
    for (const r of linkRuns(text)) {
      if (r.words && r.parts.length > 1) for (const w of r.parts) add(inRun, r.key, w);
      else add(alone, r.key, normalizeTerm(r.parts.join(' ')));
    }
  }
  const out = new Map<string, string[]>();
  for (const e of ctx.story.entities) {
    const terms = e.terms ?? [];
    const keep = terms.filter((t) => checkTerm(t) === null && !(inRun.get(e.refKey)?.has(t) && !alone.get(e.refKey)?.has(t)));
    if (keep.length !== terms.length) out.set(e.id, keep);
  }
  return out;
}
